import {
    createContext,
    useContext,
    useState,
    useEffect,
    useMemo,
    type ReactNode,
} from 'react'
import axios from 'axios'
import { api } from '../services/API'
import { invalidateRentalOwnerAccess } from '../hooks/rentalOwnerAccessStore'
import { invalidateRentalTenantAccess } from '../hooks/rentalTenantAccessStore'
import { invalidateTenantModulos } from '../hooks/useTenantModulos'
import { invalidateComposicaoEmpresa } from '../hooks/useComposicaoEmpresa'
import { invalidatePropertyAccess } from '../hooks/usePropertyAccess'
import type { Permissao, RoleSummary } from '../utils/types'

export interface User {
    id: string
    nome: string
    email: string
    /** Um utilizador pode ter vários papéis em simultâneo — nunca assumir só o primeiro. */
    roles: RoleSummary[]
    /** Estate actualmente activo nesta sessão — `null` só é possível para ADMIN. */
    estateId?: string | null
    /** Todos os estates a que este utilizador está ligado — usar para decidir se mostra o selector. */
    estateIds: string[]
    firstLogged: boolean
    /** Se este utilizador é também entidade activa do condomínio actual — calculado pelo servidor. */
    temEntidade?: boolean
}

// Empresa/tenant devolvido pelo backend quando o email de login está
// associado a mais do que um tenant (ver MultiploTenantException).
export interface EmpresaOpcao {
    tenantId: string
    schemaName: string
    nome: string
    logoUrl: string | null
}

// Lançado pelo `login` quando o backend responde 300 MULTIPLE_CHOICES —
// a UI deve apresentar `empresas` para o utilizador escolher e reenviar
// o login com o tenantId escolhido.
export class MultiploTenantError extends Error {
    empresas: EmpresaOpcao[]

    constructor(empresas: EmpresaOpcao[], message?: string) {
        super(message || 'Este email está associado a várias empresas. Escolha uma para continuar.')
        this.name = 'MultiploTenantError'
        this.empresas = empresas
    }
}

interface LoginParams {
    email: string
    password: string
    rememberMe?: boolean
    tenantId?: string
}

interface AuthContextValue {
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
    login: (params: LoginParams) => Promise<User>
    /**
     * Termina um login "Entrar com Google" que ficou pendente por o email
     * estar associado a várias empresas — o backend já verificou o email
     * pelo Google (cookie pending_google_login), só falta o tenantId
     * escolhido pelo utilizador. Ver GET /api/auth/google/tenants.
     */
    completeGoogleLogin: (tenantId: string) => Promise<User>
    completeLogin: (user: User) => void
    /**
     * Termina a sessão no servidor (expira o cookie httpOnly) e limpa o
     * estado local. É assíncrono de propósito: quem chama TEM de esperar
     * por ele antes de navegar o browser, senão o pedido é cancelado a
     * meio e a sessão continua válida.
     */
    logout: () => Promise<void>
    switchEstate: (estateId: string) => Promise<void>
    exitEstate: () => Promise<void>
    updateUser: (data: Partial<User>) => void
    /**
     * Testa a permissão contra a união das permissões de TODOS os papéis do
     * utilizador — nunca decidir visibilidade de UI pelo nome/código de um
     * papel específico, já que um utilizador pode ter vários papéis e
     * qualquer um deles pode ser customizado ou editado a qualquer momento.
     */
    hasPermission: (permissao: Permissao) => boolean
    /** `true` se o utilizador tiver pelo menos uma das permissões indicadas — útil para famílias `X_MANAGE`/`X_CRIAR`/`X_EDITAR`/`X_ELIMINAR`. */
    hasAnyPermission: (...permissoes: Permissao[]) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    // Única fonte fiável do utilizador completo — login/switch-estate/exit-estate
    // devolvem um AuthResponse mais magro (sem `temEntidade`, calculado só aqui a
    // partir do estateId já activo no JWT), por isso qualquer fluxo que troque o
    // token activo tem de passar por aqui antes de actualizar o estado local.
    const fetchMe = async (): Promise<User> => {
        const response = await api.get('/api/auth/me')
        return response.data
    }

    // A sessão vive num cookie httpOnly emitido pelo backend (partilhado
    // entre subdomínios) — o JS não o lê nem o guarda, só confirma que ainda
    // é válido perguntando ao servidor. Sem sessão válida, `/me` responde
    // 401 e o utilizador fica por autenticar (comportamento normal, não um
    // erro a reportar).
    useEffect(() => {
        async function restoreSession() {
            try {
                setUser(await fetchMe())
            } catch {
                setUser(null)
            } finally {
                setIsLoading(false)
            }
        }

        restoreSession()

        // Se a página for restaurada da bfcache do browser (ex: "Retroceder"
        // depois de um logout, ou o separador volta a ficar activo), o React
        // não re-monta e o `user` em memória fica desactualizado. Revalidar
        // contra o servidor apanha a sessão já terminada e envia para o /login.
        function revalidateOnRestore(event: PageTransitionEvent) {
            if (event.persisted) restoreSession()
        }
        window.addEventListener('pageshow', revalidateOnRestore)
        return () => window.removeEventListener('pageshow', revalidateOnRestore)
    }, [])


    const login = async ({ email, password, rememberMe, tenantId }: LoginParams) => {
        if (!email || !password) {
            throw new Error('Email e palavra-passe são obrigatórios.')
        }

        let firstLogged: boolean | undefined
        try {
            // `rememberMe` continua a seguir no pedido — o backend pode usá-lo
            // para decidir a duração do cookie de sessão que vai emitir.
            const response = await api.post<{ firstLogged?: boolean }>('/api/auth/login', {
                email, password, rememberMe, tenantId
            })
            firstLogged = response.data?.firstLogged
        } catch (err) {
            // Email associado a várias empresas — a UI deve mostrar um
            // selector com `empresas` e reenviar o login com o tenantId escolhido.
            if (axios.isAxiosError(err) && err.response?.status === 300) {
                throw new MultiploTenantError(err.response.data?.empresas ?? [], err.response.data?.message)
            }
            throw err
        }

        // O cookie de sessão já vem definido pelo próprio `Set-Cookie` desta
        // resposta, mas o corpo (AuthResponse) não traz `temEntidade` — só o
        // `/me` (UserResponse) calcula esse campo. Buscá-lo agora evita que o
        // utilizador arranque a sessão com `temEntidade` undefined até ao
        // próximo reload.
        const me = await fetchMe()
        // O `firstLogged` vem da resposta do login: o `/me` lê-o da BD, que o
        // próprio login já marcou como "já entrou" — com o do `/me`, o convite a
        // mudar a palavra-passe (Login.tsx) nunca aparecia, nem no 1.º acesso
        // nem depois de uma palavra-passe provisória dada pela gestão.
        return { ...me, firstLogged: firstLogged ?? me.firstLogged }
    }

    const completeGoogleLogin = async (tenantId: string) => {
        await api.post('/api/auth/google/complete', { tenantId })
        return fetchMe()
    }

    const completeLogin = (authenticatedUser: User) => {
        setUser(authenticatedUser)
    }

    const logout = async () => {
        // O cookie de sessão é httpOnly — o JS não o consegue apagar, por isso
        // o logout TEM de passar pelo servidor para o expirar (Set-Cookie com
        // Max-Age=0) e pôr o refresh token na blacklist.
        //
        // É essencial ESPERAR por esta resposta: se quem chama fizer um
        // `window.location.href` imediato, o browser cancela este pedido a
        // meio, o cookie fica válido e o `/api/auth/me` do próximo arranque
        // volta a autenticar o utilizador — ou seja, o "Sair" não sai.
        //
        // Se o pedido falhar/expirar (rede, endpoint indisponível), limpamos
        // na mesma o estado local para a UI nunca ficar presa.
        //
        // Sem corpo — o servidor lê o refresh token do cookie httpOnly. Não
        // enviar Content-Type: o default do axios para POST
        // (application/x-www-form-urlencoded) fazia o endpoint responder 415
        // e o logout nunca chegava a limpar os cookies de sessão.
        const terminarSessaoNoServidor = () =>
            api.post('/api/auth/logout', null, {
                timeout: 5000,
                headers: { 'Content-Type': null },
            })

        try {
            try {
                await terminarSessaoNoServidor()
            } catch {
                // Em produção o primeiro pedido pode demorar (ex.: ligação ao
                // Redis parada) e o timeout cancela-o antes de a resposta com os
                // Set-Cookie chegar — os cookies ficam válidos e o login volta a
                // autenticar sozinho. Uma segunda tentativa apanha a ligação já
                // recuperada; é o mesmo que o utilizador faria à mão.
                await terminarSessaoNoServidor()
            }
        } catch (err) {
            // Segue-se para limpar o estado local de qualquer forma — mas deixa
            // rasto, porque um logout que só limpa o cliente deixa a sessão viva
            // no servidor (cookie httpOnly + refresh token por expirar).
            console.warn('Logout no servidor falhou; a terminar a sessão apenas localmente.', err)
        }
        setUser(null)
        // A cache de módulos é por tenant — descartá-la para o próximo login
        // (possivelmente noutra empresa) não herdar os módulos deste.
        invalidateTenantModulos()
        invalidateRentalOwnerAccess()
        invalidateRentalTenantAccess()
        invalidateComposicaoEmpresa()
        invalidatePropertyAccess()
    }

    const switchEstate = async (estateId: string) => {
        // O corpo desta resposta já traz o novo estateId, mas não `temEntidade`
        // (só o `/me` calcula esse campo) — usá-lo directamente apagaria o
        // valor correcto que o `/me` inicial já tinha posto no estado. Ver
        // comentário em `fetchMe`.
        await api.post('/api/auth/switch-estate', { estateId })
        setUser(await fetchMe())
    }

    const exitEstate = async () => {
        await api.post('/api/auth/exit-estate', { user })
        setUser(await fetchMe())
    }

    const updateUser = (data: Partial<User>) => {
        setUser((prev) => ({ ...prev, ...data } as User))
    }

    // União das permissões de todos os papéis do utilizador — calculada uma
    // vez por sessão/actualização de user, não a cada chamada de hasPermission.
    const effectivePermissions = useMemo(
        () => new Set(user?.roles?.flatMap((r) => r.permissoes) ?? []),
        [user]
    )
    const hasPermission = (permissao: Permissao) => effectivePermissions.has(permissao)
    const hasAnyPermission = (...permissoes: Permissao[]) => permissoes.some((p) => effectivePermissions.has(p))

    return (
        <AuthContext.Provider
            value={{ user, isAuthenticated: !!user, isLoading, login, completeGoogleLogin, completeLogin, logout, switchEstate, exitEstate, updateUser, hasPermission, hasAnyPermission }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth deve ser usado dentro de um <AuthProvider>.')
    return ctx
}
